import React, { useMemo, type PropsWithChildren } from 'react';

import Box from '@mui/material/Box';

import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import Typography, { type Props as TypographyProps } from './Typography';

type ReactComponentChild = React.ReactElement & { type: React.JSXElementConstructor<unknown> };
type NullableReactComponentChild = ReactComponentChild | null | false;

type CalloutContainerProps = {
    $backgroundColor: string;
    $borderRadius: string;
    $width?: string;
    $showBorder?: boolean;
};
const CalloutContainer = styledComponent(Box)<CalloutContainerProps>`
    display: flex;
    padding: 1rem;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: ${({ $borderRadius }) => $borderRadius};
    ${({ $width }) => $width && `width: ${$width};`}
    ${({ $showBorder }) => $showBorder && `border: 1px solid ${colours.grey.G200};`}
`;

const TitleContentContainer = styledComponent(Box)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

type CalloutProps = {
    fullWidth?: boolean;
    backgroundColor?: string;
    showBorder?: boolean;
    alignItems?: 'center' | 'flex-start' | 'flex-end' | 'stretch' | 'baseline';
    children: NullableReactComponentChild | NullableReactComponentChild[];
};

const Callout: React.FC<CalloutProps> = ({
    fullWidth,
    backgroundColor = colours.grey.G100,
    showBorder,
    alignItems,
    children,
}) => {
    const { leading, trailing, title, content } = useMemo(() => {
        const getComponentFromChildren = (componentName: string) =>
            (Array.isArray(children) ? children : [children]).find(
                (child) => child && child.type.name === componentName,
            );

        const leadingComponent = getComponentFromChildren(CalloutLeading.name);
        const trailingComponent = getComponentFromChildren(CalloutTrailing.name);
        const titleComponent = getComponentFromChildren(CalloutTitle.name);
        const contentComponent = getComponentFromChildren(CalloutContent.name);

        return {
            leading: leadingComponent,
            trailing: trailingComponent,
            title: titleComponent,
            content: contentComponent,
        };
    }, [children]);

    return (
        <CalloutContainer
            alignItems={alignItems}
            $borderRadius="0.5rem"
            $width={fullWidth ? '100%' : 'fit-content'}
            $backgroundColor={backgroundColor}
            $showBorder={showBorder}
        >
            {Boolean(leading) && <Box marginRight="0.5rem">{leading}</Box>}
            <TitleContentContainer>
                {title}
                {content}
            </TitleContentContainer>
            {Boolean(trailing) && <Box marginRight="0.5rem">{trailing}</Box>}
        </CalloutContainer>
    );
};

type CalloutTitleProps = {
    variant?: TypographyProps['variant'];
    color?: TypographyProps['color'];
};
export const CalloutTitle: React.FC<PropsWithChildren<CalloutTitleProps>> = ({
    variant = 'h5',
    color = colours.primary.main,
    children,
}) => (
    <Typography variant={variant} color={color}>
        {children}
    </Typography>
);

export const CalloutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => <Box>{children}</Box>;

export const CalloutLeading: React.FC<{ children: React.ReactNode }> = ({ children }) => <Box>{children}</Box>;

export const CalloutTrailing: React.FC<{ children: React.ReactNode }> = ({ children }) => <Box>{children}</Box>;

export default Callout;
